import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import CollapsedControllerContext from './collapsed-controller-context';
import { useUpdateSideMenuMutation } from '@/app/services/solution';

const getResizedSideMenuState = (previous: {
  persistent: boolean;
  collapsed: boolean;
  sideMenuKey: number;
}): { persistent: boolean; collapsed: boolean; sideMenuKey: number } => ({
  collapsed: previous.collapsed,
  persistent: window.innerWidth >= 992,
  sideMenuKey: previous.sideMenuKey + 1,
});

const CollapsedControllerProvider = ({ children }: { readonly children: ReactNode }): ReactElement => {
  const location = useLocation();
  const [updateSideMenu] = useUpdateSideMenuMutation();

  const [values, setValues] = useState({
    persistent: true,
    collapsed: true,
    sideMenuKey: 0,
  });

  useEffect(() => {
    queueMicrotask(() => {
      setValues((previous) => ({ ...previous, sideMenuKey: previous.sideMenuKey + 1 }));
    });
  }, [location]);

  useEffect(() => {
    const onResize = (): (() => void) => {
      const timer = setTimeout(() => {
        setValues(getResizedSideMenuState);
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const toggleSideMenu = useCallback((): void => {
    setValues((previous) => {
      void updateSideMenu({ isSideMenuOpen: previous.collapsed });
      return { ...previous, collapsed: !previous.collapsed };
    });
  }, [updateSideMenu]);

  const value = useMemo(() => ({ ...values, toggleSideMenu }), [values, toggleSideMenu]);

  return <CollapsedControllerContext value={value}>{children}</CollapsedControllerContext>;
};

export default CollapsedControllerProvider;
